// Signup attribution: where a new user came from, captured once on landing and sent with the
// signup request (HubSpot lead fields, mayainsights/mayainsights#1820) and on the GTM
// sign_up_completed event (GA4 stitching, mayainsights/mayainsights#1819).
//
// First touch wins inside the browser, except that a fresh landing that carries UTM
// parameters replaces an older one (a new campaign click is the better answer). Stored in
// localStorage so it survives the Microsoft redirect and a return visit before signup.

const KEY = 'maya-signup-attribution'
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

export type LeadSource = 'shopify_app_store' | 'website' | 'direct'

export interface SignupAttribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  intent?: string
  shop?: string
  landing_url?: string
  landing_referrer?: string
  lead_source?: LeadSource
}

const read = (): SignupAttribution => {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || '{}') || {}
  } catch {
    return {}
  }
}

const write = (value: SignupAttribution) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value))
  } catch {
    /* storage blocked: attribution is best-effort */
  }
}

const leadSourceFor = (a: SignupAttribution, referrer: string): LeadSource => {
  if (a.shop || /(^|\.)shopify\.com$/i.test(hostOf(referrer)) || /myshopify\.com$/i.test(hostOf(referrer))) {
    return 'shopify_app_store'
  }
  if (/(^|\.)mayainsights\.com$/i.test(hostOf(referrer)) && !/^app\./i.test(hostOf(referrer))) {
    return 'website'
  }
  if (a.intent || a.utm_source) return 'website'
  return 'direct'
}

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname
  } catch {
    return ''
  }
}

/** Call once on app load. Cheap and idempotent. */
export const captureSignupAttribution = (href = window.location.href, referrer = document.referrer) => {
  let params: URLSearchParams
  try {
    params = new URL(href).searchParams
  } catch {
    return
  }
  const fresh: SignupAttribution = {}
  UTM.forEach((k) => {
    const v = params.get(k)
    if (v) fresh[k] = v.slice(0, 200)
  })
  const intent = params.get('intent')
  if (intent) fresh.intent = intent.slice(0, 40)
  const shop = params.get('shop')
  if (shop) fresh.shop = shop.slice(0, 200)

  const stored = read()
  const hasStored = Object.keys(stored).length > 0
  const freshHasUtm = UTM.some((k) => fresh[k])
  if (hasStored && !freshHasUtm && !fresh.shop && !fresh.intent) return

  const next: SignupAttribution = freshHasUtm || !hasStored ? { ...fresh } : { ...stored, ...fresh }
  next.landing_url = next.landing_url || href.slice(0, 500)
  next.landing_referrer = next.landing_referrer ?? (referrer || '').slice(0, 500)
  next.lead_source = leadSourceFor(next, next.landing_referrer || '')
  write(next)
}

export const getSignupAttribution = (): SignupAttribution => read()

export const clearSignupAttribution = () => {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}
